import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  Code,
  Copy,
  Download,
  Edit3,
  Link,
  Linkedin,
  PlayCircle,
  Redo2,
  Save,
  Share,
  Undo2,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { TEST_IDS } from "../../constants/testIds";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { getSession } from "@/lib/supabase";
import { useCanvas } from "../../contexts/CanvasContext";
import { useCanvasIntegration } from "../../hooks/useCanvasIntegration";
import { setCanvasShared } from "../../utils/shareUtils";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// Helper function to extract actual code content from markdown
function extractCodeContent(content) {
  if (!content || typeof content !== "string") return "";

  // Check for code block patterns (```language\ncode\n```)
  const codeBlockMatch = content.match(/```[\w]*\n([\s\S]*?)\n```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  // If no code block found, return the content as-is
  return content.trim();
}

// Language to file extension mapping
const LANGUAGE_EXTENSIONS = {
  javascript: ".js",
  jsx: ".js",
  typescript: ".ts",
  tsx: ".ts",
  python: ".py",
  html: ".html",
  css: ".css",
  json: ".json",
  java: ".java",
  go: ".go",
  rust: ".rs",
  ruby: ".rb",
  php: ".php",
  swift: ".swift",
  kotlin: ".kt",
  c: ".c",
  cpp: ".cpp",
  csharp: ".cs",
  shell: ".sh",
  bash: ".sh",
  sql: ".sql",
  yaml: ".yaml",
  xml: ".xml",
  markdown: ".md",
};

// Get language display label
function getLanguageLabel(language) {
  const labels = {
    javascript: "JavaScript",
    jsx: "JavaScript",
    typescript: "TypeScript",
    tsx: "TypeScript",
    python: "Python",
    html: "HTML",
    css: "CSS",
    json: "JSON",
    java: "Java",
    go: "Go",
    rust: "Rust",
    ruby: "Ruby",
    php: "PHP",
    swift: "Swift",
    kotlin: "Kotlin",
    c: "C",
    cpp: "C++",
    csharp: "C#",
    shell: "Shell",
    bash: "Bash",
    sql: "SQL",
    yaml: "YAML",
    xml: "XML",
    markdown: "Markdown",
  };
  return labels[language] || language?.charAt(0).toUpperCase() + language?.slice(1) || "Source";
}

// Get MIME type for language
function getMimeType(language) {
  const mimeTypes = {
    javascript: "text/javascript",
    jsx: "text/javascript",
    typescript: "text/typescript",
    tsx: "text/typescript",
    python: "text/x-python",
    html: "text/html",
    css: "text/css",
    json: "application/json",
    java: "text/x-java-source",
    go: "text/x-go",
    rust: "text/x-rust",
    ruby: "text/x-ruby",
    php: "text/x-php",
    swift: "text/x-swift",
    kotlin: "text/x-kotlin",
    c: "text/x-c",
    cpp: "text/x-c++",
    csharp: "text/x-csharp",
    shell: "text/x-shellscript",
    bash: "text/x-shellscript",
    sql: "text/x-sql",
    yaml: "text/yaml",
    xml: "application/xml",
    markdown: "text/markdown",
  };
  return mimeTypes[language] || "text/plain";
}

// Get download options based on content type and language
function getDownloadOptions(contentType, language) {
  switch (contentType) {
    case "code": {
      const ext = LANGUAGE_EXTENSIONS[language] || ".txt";
      const langLabel = getLanguageLabel(language);
      return [
        { id: "pdf", label: "PDF Document", ext: ".pdf" },
        { id: "source", label: `${langLabel} File (${ext})`, ext },
        { id: "markdown", label: "Markdown (with code fence)", ext: ".md" },
      ];
    }
    case "spreadsheet":
      return [
        { id: "pdf", label: "PDF Document", ext: ".pdf" },
        { id: "excel", label: "Excel Spreadsheet", ext: ".xlsx" },
        { id: "csv", label: "CSV File", ext: ".csv" },
      ];
    case "html":
      return [
        { id: "pdf", label: "PDF Document", ext: ".pdf" },
        { id: "html", label: "HTML File", ext: ".html" },
        { id: "markdown", label: "Markdown", ext: ".md" },
      ];
    case "text":
    case "markdown":
    default:
      return [
        { id: "pdf", label: "PDF Document", ext: ".pdf" },
        { id: "docx", label: "Word Document", ext: ".docx" },
        { id: "markdown", label: "Markdown", ext: ".md" },
      ];
  }
}

// Version Dropdown Component - COMMENTED OUT FOR NOW
// const VersionDropdown = memo(
//   ({ canvasName, canvasVersion, onVersionSelect }) => {
//     const { getAvailableVersions, loadCanvasVersion } = useCanvas();
//     const [versions, setVersions] = useState([]);
//     const [isLoading, setIsLoading] = useState(false);

//     // Load available versions when dropdown opens
//     const loadVersions = useCallback(async () => {
//       if (isLoading) return;

//       setIsLoading(true);
//       try {
//         const availableVersions = await getAvailableVersions();

//         // Sort versions by timestamp (newest first) and ensure first version is included
//         const sortedVersions = availableVersions.sort(
//           (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
//         );

//         setVersions(sortedVersions);
//       } catch (error) {
//         console.error("Failed to load versions:", error);
//         toast.error("Failed to load version history");
//       } finally {
//         setIsLoading(false);
//       }
//     }, [getAvailableVersions, isLoading]);

//     const handleVersionSelect = useCallback(
//       async (versionId) => {
//         try {
//           const success = await loadCanvasVersion(versionId);
//           if (success) {
//             const selectedVersion = versions.find((v) => v.id === versionId);
//             toast.success(
//               `Loaded ${selectedVersion?.name || "Canvas"} v${selectedVersion?.version || 1}`,
//             );
//             onVersionSelect?.(versionId);
//           } else {
//             toast.error("Failed to load selected version");
//           }
//         } catch (error) {
//           console.error("Error loading version:", error);
//           toast.error("Failed to load selected version");
//         }
//       },
//       [loadCanvasVersion, versions, onVersionSelect],
//     );

//     const formatTimestamp = (timestamp) => {
//       try {
//         const date = new Date(timestamp);
//         const now = new Date();
//         const diffMs = now - date;
//         const diffMins = Math.floor(diffMs / 60000);
//         const diffHours = Math.floor(diffMs / 3600000);
//         const diffDays = Math.floor(diffMs / 86400000);

//         if (diffMins < 1) return "Just now";
//         if (diffMins < 60) return `${diffMins}m ago`;
//         if (diffHours < 24) return `${diffHours}h ago`;
//         if (diffDays < 7) return `${diffDays}d ago`;
//         return date.toLocaleDateString();
//       } catch {
//         return "Unknown";
//       }
//     };

//     const getVersionTypeIcon = (version) => {
//       const toolCallType = version.metadata?.toolCallType;
//       if (toolCallType === "canvas_create") return "🎨";
//       if (toolCallType === "canvas_edit") return "✏️";
//       if (toolCallType === "canvas_stream") return "📝";
//       if (version.metadata?.isInitialVersion) return "🆕";
//       if (version.metadata?.createdFromTool) return "🎨";
//       if (version.metadata?.createdFromStream) return "📝";
//       if (version.metadata?.createdFromStreamUpdate) return "📝";
//       return "📄";
//     };

//     const getVersionDescription = (version) => {
//       const metadata = version.metadata || {};
//       const parts = [];

//       if (metadata.hasBlocks) {
//         parts.push(`${metadata.blocksCount} blocks`);
//       } else if (metadata.contentLength > 0) {
//         parts.push(`${metadata.contentLength} chars`);
//       }

//       if (version.type && version.type !== "text") {
//         parts.push(version.type);
//       }

//       if (version.language && version.language !== "javascript") {
//         parts.push(version.language);
//       }

//       // Add additional context for different version types
//       if (metadata.isInitialVersion) {
//         parts.push("Initial");
//       } else if (metadata.isEdit) {
//         parts.push("Edited");
//       } else if (metadata.createdFromStream) {
//         parts.push("Streamed");
//       }

//       return parts.length > 0 ? parts.join(" • ") : "Canvas content";
//     };

//     return (
//       <DropdownMenu onOpenChange={(open) => open && loadVersions()}>
//         <DropdownMenuTrigger asChild>
//           <Button
//             variant="ghost"
//             size="sm"
//             className="gap-2 text-white hover:bg-transparent bg-transparent border-0 shadow-none"
//           >
//             <span className="text-sm font-medium">v{canvasVersion || 1}</span>
//             <ChevronDown className="h-4 w-4 text-gray-400" />
//           </Button>
//         </DropdownMenuTrigger>
//         <DropdownMenuContent
//           align="start"
//           className="w-80 bg-transparent backdrop-blur-md border-gray-600 shadow-lg"
//           style={{
//             zIndex: 1000,
//           }}
//         >
//           <DropdownMenuLabel className="text-white">
//             Version History
//           </DropdownMenuLabel>
//           <DropdownMenuSeparator className="bg-gray-600" />

//           {isLoading ? (
//             <DropdownMenuItem disabled className="text-white">
//               <Clock className="h-4 w-4 mr-2" />
//               Loading versions...
//             </DropdownMenuItem>
//           ) : versions.length === 0 ? (
//             <DropdownMenuItem disabled className="text-white">
//               No version history available
//             </DropdownMenuItem>
//           ) : (
//             versions.map((version, index) => (
//               <DropdownMenuItem
//                 key={version.id}
//                 onClick={() => handleVersionSelect(version.id)}
//                 className="flex items-start justify-between py-3 px-3 text-white hover:bg-white/10 cursor-pointer"
//               >
//                 <div className="flex flex-col flex-1 min-w-0">
//                   <div className="flex items-center gap-2 mb-1">
//                     <span className="text-sm">
//                       {getVersionTypeIcon(version)}
//                     </span>
//                     <span className="font-medium text-sm truncate">
//                       {version.name} v{version.version}
//                     </span>
//                     {index === 0 && (
//                       <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
//                     )}
//                   </div>
//                   <div className="text-xs text-gray-400 mb-1">
//                     {getVersionDescription(version)}
//                   </div>
//                   {version.metadata?.contentPreview && (
//                     <div className="text-xs text-gray-500 truncate max-w-full">
//                       {version.metadata.contentPreview}
//                     </div>
//                   )}
//                   <div className="text-xs text-gray-400 mt-1">
//                     {formatTimestamp(version.timestamp)}
//                   </div>
//                 </div>
//                 <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
//                   {version.metadata?.isEdit && (
//                     <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
//                       Edit
//                     </span>
//                   )}
//                   {version.metadata?.fromDatabase && (
//                     <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
//                       Saved
//                     </span>
//                   )}
//                   {version.metadata?.isInitialVersion && (
//                     <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">
//                       Initial
//                     </span>
//                   )}
//                 </div>
//               </DropdownMenuItem>
//             ))
//           )}
//         </DropdownMenuContent>
//       </DropdownMenu>
//     );
//   },
// );

// VersionDropdown.displayName = "VersionDropdown";

// Custom SVG Icon for Twitter/X
const TwitterXIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

TwitterXIcon.propTypes = {
  className: PropTypes.string,
};

// Canvas Share Dialog Component
const CanvasShareDialog = memo(
  ({ open, onOpenChange, content, title, sourceMessageId, canvasType }) => {
    const [windowWidth] = useState(
      typeof window !== "undefined" ? window.innerWidth : 640
    );
    const [shareUrl, setShareUrl] = useState(null);
    const [isSharing, setIsSharing] = useState(false);
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    // Generate share link when dialog opens
    const generateShareLink = useCallback(async () => {
      if (!sourceMessageId || isSharing) return;

      try {
        setIsSharing(true);

        const {
          success,
          error,
          shareUrl: generatedUrl,
        } = await setCanvasShared(sourceMessageId, true);

        if (error) {
          throw error;
        }

        if (success && generatedUrl) {
          setShareUrl(generatedUrl);
        } else {
          throw new Error("Failed to generate share link");
        }
      } catch (error) {
        console.error("Error generating canvas share link:", error);
        toast.error("Failed to generate share link");
      } finally {
        setIsSharing(false);
      }
    }, [sourceMessageId, isSharing]);

    // Generate share link when dialog opens and we have a source message ID
    useEffect(() => {
      if (open && sourceMessageId && !shareUrl && !isSharing) {
        generateShareLink();
      }
    }, [open, sourceMessageId, shareUrl, isSharing, generateShareLink]);

    // Handle copy link
    const handleCopyLink = async (text) => {
      try {
        let textToCopy;

        // If we have a share URL, use that instead of content
        if (shareUrl) {
          textToCopy = shareUrl;
        } else {
          // For spreadsheets, offer markdown table format for sharing
          if (canvasType === "spreadsheet") {
            textToCopy = convertCsvToMarkdownTable(text);
            if (!textToCopy.trim()) {
              textToCopy = text; // Fallback to original content
            }
          } else {
            textToCopy = text;
          }
        }

        await navigator.clipboard.writeText(textToCopy);

        if (shareUrl) {
          toast.success("Share link copied to clipboard");
        } else if (canvasType === "spreadsheet") {
          toast.success("Spreadsheet copied as markdown table");
        } else {
          toast.success("Content copied to clipboard");
        }

        // Show copy success animation
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 1500);
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        toast.error("Failed to copy to clipboard");
      }
    };

    // Helper function to convert CSV to markdown table (same as in CanvasContext)
    const convertCsvToMarkdownTable = (csvContent) => {
      if (!csvContent || typeof csvContent !== "string") {
        return "";
      }

      try {
        const lines = csvContent.trim().split("\n");
        if (lines.length === 0) {
          return "";
        }

        const parseCSVLine = (line) => {
          const result = [];
          let current = "";
          let inQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === "," && !inQuotes) {
              result.push(current.trim());
              current = "";
            } else {
              current += char;
            }
          }

          result.push(current.trim());
          return result;
        };

        const rows = lines.map(parseCSVLine);
        const nonEmptyRows = rows.filter((row) =>
          row.some((cell) => cell && cell.trim().length > 0)
        );

        if (nonEmptyRows.length === 0) {
          return "";
        }

        const markdownLines = [];

        nonEmptyRows.forEach((row, index) => {
          const escapedRow = row.map((cell) =>
            (cell || "").replace(/\|/g, "\\|")
          );

          markdownLines.push(`| ${escapedRow.join(" | ")} |`);

          if (index === 0) {
            const separators = escapedRow.map(() => "---");
            markdownLines.push(`| ${separators.join(" | ")} |`);
          }
        });

        return markdownLines.join("\n");
      } catch (error) {
        console.error("Error converting CSV to markdown table:", error);
        return csvContent;
      }
    };

    // Handle social media sharing
    const handleSocialShare = (platform, text) => {
      let contentToShare;

      // Use share URL if available, otherwise fall back to content
      if (shareUrl) {
        contentToShare = `Check out this shared canvas: ${title}\n${shareUrl}`;
      } else {
        // For spreadsheets, use markdown table format for social sharing
        if (canvasType === "spreadsheet") {
          const markdownTable = convertCsvToMarkdownTable(text);
          contentToShare = markdownTable.trim() ? markdownTable : text;
        } else {
          contentToShare = text;
        }
      }

      const encodedText = encodeURIComponent(contentToShare);

      let shareUrlForPlatform = "";

      switch (platform) {
        case "twitter":
          shareUrlForPlatform = `https://twitter.com/intent/tweet?text=${encodedText}`;
          break;
        case "linkedin":
          if (shareUrl) {
            shareUrlForPlatform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
          } else {
            shareUrlForPlatform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
          }
          break;
        case "reddit":
          shareUrlForPlatform = `https://reddit.com/submit?title=${encodeURIComponent(title || "Shared Canvas")}&text=${encodedText}`;
          break;
        default:
          return;
      }

      window.open(shareUrlForPlatform, "_blank", "width=600,height=400");
    };

    // Truncate content for display - responsive truncation
    const getResponsiveTruncation = () => {
      if (!content) return "";

      let displayContent = content;

      // For spreadsheets, show a preview in markdown table format
      if (canvasType === "spreadsheet") {
        const markdownTable = convertCsvToMarkdownTable(content);
        if (markdownTable.trim()) {
          displayContent = markdownTable;
        }
      }

      // Use different truncation lengths based on screen size
      const isMobile = windowWidth < 640; // sm breakpoint
      const maxLength = isMobile ? 120 : 250;

      return displayContent.length > maxLength
        ? displayContent.substring(0, maxLength) + "..."
        : displayContent;
    };

    const truncatedContent = getResponsiveTruncation();

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[90vw] sm:max-w-md md:max-w-lg lg:max-w-xl p-0 bg-[#1a1a1a] border-0 rounded-2xl"
          style={{ zIndex: 9999 }}
          hideCloseButton={true}
        >
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 h-7 w-7 sm:h-8 sm:w-8 text-white bg-transparent hover:bg-white/10"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>

            {/* Header */}
            <div className="p-4 sm:p-6 pb-3 sm:pb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-white break-words">
                {title &&
                  title.split(" ").slice(0, 5).join(" ") +
                    (title && title.split(" ").length > 5 ? "..." : "")}
              </h2>
              {shareUrl && (
                <p className="text-sm text-gray-400 mt-2">
                  Share this canvas with others using the link below
                </p>
              )}
              {isSharing && (
                <p className="text-sm text-gray-400 mt-2">
                  Generating share link...
                </p>
              )}
            </div>

            {/* Share URL Display */}
            {shareUrl && (
              <div className="px-4 sm:px-6 pb-4">
                <div className="bg-[#2a2a2a] rounded-lg p-3 border border-gray-600">
                  <div className="text-gray-300 text-xs sm:text-sm break-all">
                    {shareUrl}
                  </div>
                </div>
              </div>
            )}

            {/* Text Content Preview */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="bg-[#2a2a2a] rounded-lg p-3 sm:p-4 border border-gray-600 max-h-[30vh] overflow-y-auto">
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {truncatedContent}
                </div>
              </div>
            </div>

            {/* Share Options */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {/* Copy Link */}
                <button
                  onClick={() => handleCopyLink(content)}
                  className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors relative"
                  disabled={isSharing}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                    <AnimatePresence mode="wait">
                      {showCopySuccess ? (
                        <motion.div
                          key="check"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="link"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Link className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </button>

                {/* Twitter/X */}
                <button
                  onClick={() => handleSocialShare("twitter", content)}
                  className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
                  disabled={isSharing}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                    <TwitterXIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                </button>

                {/* LinkedIn */}
                <button
                  onClick={() => handleSocialShare("linkedin", content)}
                  className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
                  disabled={isSharing}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                    <Linkedin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                </button>

                {/* Reddit */}
                <button
                  onClick={() => handleSocialShare("reddit", content)}
                  className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
                  disabled={isSharing}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                    <svg
                      className="h-5 w-5 sm:h-6 sm:w-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Labels */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center">
                  <span className="text-xs text-gray-400">
                    {shareUrl
                      ? "Copy link"
                      : canvasType === "spreadsheet"
                        ? "Copy as table"
                        : "Copy content"}
                  </span>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">X</span>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">LinkedIn</span>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">Reddit</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

CanvasShareDialog.displayName = "CanvasShareDialog";

CanvasShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  content: PropTypes.string,
  title: PropTypes.string,
  sourceMessageId: PropTypes.string,
  canvasType: PropTypes.string,
};

const CanvasToolbar = memo(({ onClose }) => {
  const [isLoading, setIsLoading] = useState({
    save: false,
    copy: false,
    download: false,
    rename: false,
  });

  // Rename state management
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [showRenameDropdown, setShowRenameDropdown] = useState(false);
  const inputRef = useRef(null);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Copy success animation state
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const { requestCanvasStream } = useCanvasIntegration();

  const {
    canvasContent,
    canvasName,
    canvasVersion: _canvasVersion,
    canvasType,
    canvasIsDirty,
    canvasIsLoading,
    canvasSourceMessageId,
    showPreview,
    setShowPreview,
    saveCanvas,
    resetCanvas,
    copyCanvasContent,
    openCanvas,
    canvasState,
    renameCanvas,
    updateContent,
  } = useCanvas();

  // Check if undo/redo operations are available
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Track previous canvas state for emergency restoration
  const [previousCanvasState, setPreviousCanvasState] = useState(null);

  // Track previous editor state before undo operations
  const captureEditorState = useCallback(() => {
    try {
      const editor = window.canvasBlockNoteEditor;
      if (!editor || !editor.document) {
        return null;
      }

      return {
        document: JSON.parse(JSON.stringify(editor.document)), // Deep copy
        timestamp: new Date().toISOString(),
        canvasContent: canvasContent,
        canvasName: canvasName,
      };
    } catch (error) {
      console.warn("🎨 CanvasToolbar: Failed to capture editor state:", error);
      return null;
    }
  }, [canvasContent, canvasName]);

  // Restore previous editor state in case of emergency
  const restoreEditorState = useCallback(
    (savedState) => {
      try {
        const editor = window.canvasBlockNoteEditor;
        if (!editor || !savedState) {
          console.warn(
            "🎨 CanvasToolbar: Cannot restore - no editor or saved state"
          );
          return false;
        }

        // Restore the document using BlockNote's replaceBlocks method
        if (savedState.document && Array.isArray(savedState.document)) {
          editor.replaceBlocks(editor.document, savedState.document);

          // Also update canvas context if possible
          if (typeof updateContent === "function") {
            updateContent(savedState.canvasContent, "restore");
          }

          toast.success("Canvas content restored to previous state");
          return true;
        }

        return false;
      } catch (error) {
        console.error("🎨 CanvasToolbar: Error restoring editor state:", error);
        toast.error("Failed to restore canvas content");
        return false;
      }
    },
    [updateContent]
  );

  // Check if editor content is empty or minimal
  const isEditorEmpty = useCallback(() => {
    try {
      const editor = window.canvasBlockNoteEditor;
      if (!editor || !editor.document) {
        return true;
      }

      const document = editor.document;

      // Check if document is empty
      if (!document || document.length === 0) {
        return true;
      }

      // Check if only one block with no meaningful content
      if (document.length === 1) {
        const firstBlock = document[0];
        if (
          !firstBlock ||
          !firstBlock.content ||
          (Array.isArray(firstBlock.content) &&
            firstBlock.content.length === 0) ||
          (typeof firstBlock.content === "string" &&
            firstBlock.content.trim() === "") ||
          (Array.isArray(firstBlock.content) &&
            firstBlock.content.length === 1 &&
            (!firstBlock.content[0] ||
              !firstBlock.content[0].text ||
              firstBlock.content[0].text.trim() === ""))
        ) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.warn(
        "🎨 CanvasToolbar: Error checking if editor is empty:",
        error
      );
      return true; // Assume empty on error for safety
    }
  }, []);

  // Monitor editor content changes to detect empty state after undo
  useEffect(() => {
    const checkForEmptyContent = () => {
      if (isEditorEmpty() && previousCanvasState) {
        console.warn(
          "🎨 CanvasToolbar: Detected empty content after operation, attempting restore..."
        );

        // Restore previous state
        const restored = restoreEditorState(previousCanvasState);
        if (restored) {
          // Clear the previous state after successful restore
          setPreviousCanvasState(null);
        }
      }
    };

    // Check immediately
    checkForEmptyContent();

    // Set up periodic checking for empty content (with throttling)
    const interval = setInterval(checkForEmptyContent, 500);

    return () => clearInterval(interval);
  }, [isEditorEmpty, previousCanvasState, restoreEditorState]);

  // Handle undo functionality using BlockNote's built-in method
  const handleUndo = useCallback(() => {
    try {
      const editor = window.canvasBlockNoteEditor;
      if (!editor) {
        console.warn(
          "🎨 CanvasToolbar: BlockNote editor not available for undo"
        );
        return;
      }

      // CAPTURE CURRENT STATE before undo operation
      const currentState = captureEditorState();
      if (currentState) {
        setPreviousCanvasState(currentState);
      }

      // SAFEGUARD: Check if undoing would result in empty content
      // This prevents the infinite skeleton loader issue
      if (editor.document && editor.document.length <= 1) {
        const firstBlock = editor.document[0];
        if (
          !firstBlock ||
          !firstBlock.content ||
          (Array.isArray(firstBlock.content) &&
            firstBlock.content.length === 0) ||
          (typeof firstBlock.content === "string" &&
            firstBlock.content.trim() === "") ||
          (Array.isArray(firstBlock.content) &&
            firstBlock.content.length === 1 &&
            firstBlock.content[0]?.text?.trim() === "")
        ) {
          console.warn(
            "🎨 CanvasToolbar: Preventing undo that would result in empty content"
          );
          toast.warning("Cannot undo further - reached initial content");
          // Clear the captured state since we're not proceeding
          setPreviousCanvasState(null);
          return;
        }
      }

      // Perform the undo operation
      let undoSuccessful = false;

      // Method 1: Try BlockNote's built-in undo method
      if (typeof editor.undo === "function") {
        editor.undo();
        undoSuccessful = true;
      }
      // Method 2: Try TipTap editor's undo command
      else if (editor._tiptapEditor && editor._tiptapEditor.commands) {
        const tiptapEditor = editor._tiptapEditor;
        if (typeof tiptapEditor.commands.undo === "function") {
          tiptapEditor.commands.undo();
          undoSuccessful = true;
        }
      }

      if (!undoSuccessful) {
        console.warn("🎨 CanvasToolbar: No undo method available");
        // Clear the captured state since undo failed
        setPreviousCanvasState(null);
        return;
      }

      // Schedule a check for empty content after undo completes
      setTimeout(() => {
        if (isEditorEmpty() && currentState) {
          console.warn(
            "🎨 CanvasToolbar: Undo resulted in empty content, restoring..."
          );
          restoreEditorState(currentState);
          setPreviousCanvasState(null);
        }
      }, 100); // Small delay to let undo complete
    } catch (error) {
      console.error("🎨 CanvasToolbar: Error performing undo:", error);
      // Clear the captured state on error
      setPreviousCanvasState(null);
    }
  }, [captureEditorState, isEditorEmpty, restoreEditorState]);

  // Handle redo functionality using BlockNote's built-in method
  const handleRedo = useCallback(() => {
    try {
      const editor = window.canvasBlockNoteEditor;
      if (!editor) {
        console.warn(
          "🎨 CanvasToolbar: BlockNote editor not available for redo"
        );
        return;
      }

      // Method 1: Try BlockNote's built-in redo method
      if (typeof editor.redo === "function") {
        editor.redo();
        return;
      }

      // Method 2: Try TipTap editor's redo command
      if (editor._tiptapEditor && editor._tiptapEditor.commands) {
        const tiptapEditor = editor._tiptapEditor;
        if (typeof tiptapEditor.commands.redo === "function") {
          tiptapEditor.commands.redo();
          return;
        }
      }

      console.warn("🎨 CanvasToolbar: No redo method available");
    } catch (error) {
      console.error("🎨 CanvasToolbar: Error performing redo:", error);
    }
  }, []);

  // Function to clear editor's undo history
  const clearEditorUndoHistory = useCallback(() => {
    try {
      const editor = window.canvasBlockNoteEditor;
      if (!editor) {
        // Editor not available - caller should handle retry logic
        return;
      }

      // Method 1: Try TipTap editor's clearHistory command
      if (editor._tiptapEditor && editor._tiptapEditor.commands) {
        const tiptapEditor = editor._tiptapEditor;
        if (typeof tiptapEditor.commands.clearHistory === "function") {
          tiptapEditor.commands.clearHistory();
        } else {
          // Alternative: Try to manipulate history state directly
          try {
            if (tiptapEditor.state && tiptapEditor.state.history) {
              // Reset history state if accessible
              const history = tiptapEditor.state.history;
              if (history.done) history.done.length = 0;
              if (history.undone) history.undone.length = 0;
            }
          } catch (historyError) {
            console.warn(
              "🎨 CanvasToolbar: Could not clear history via state manipulation:",
              historyError
            );
          }
        }
      }

      // Method 2: Try BlockNote's built-in history clear method if available
      if (typeof editor.clearHistory === "function") {
        editor.clearHistory();
      }

      // Force update undo/redo state after clearing
      setCanUndo(false);
      setCanRedo(false);
    } catch (error) {
      console.error("🎨 CanvasToolbar: Error clearing undo history:", error);
    }
  }, []);

  // Listen for canvas reset undo history events
  useEffect(() => {
    const handleResetUndoHistory = () => {
      // Retry mechanism to wait for editor to be available
      const attemptClearHistory = (attempt = 1, maxAttempts = 5) => {
        const editor = window.canvasBlockNoteEditor;

        if (editor) {
          // Editor is available, clear the history
          clearEditorUndoHistory();

          // Additional safety: Force disable buttons after clearing
          setCanUndo(false);
          setCanRedo(false);

          // Clear previous canvas state to prevent stale restoration
          setPreviousCanvasState(null);
        } else if (attempt < maxAttempts) {
          // Editor not ready yet, retry with exponential backoff
          const delay = Math.min(50 * Math.pow(2, attempt - 1), 500); // 50ms, 100ms, 200ms, 400ms, 500ms
          setTimeout(() => attemptClearHistory(attempt + 1, maxAttempts), delay);
        } else {
          // Max attempts reached, editor still not available
          // This is only logged once after all retries exhausted
          console.warn(
            "🎨 CanvasToolbar: BlockNote editor not available after multiple retry attempts"
          );
        }
      };

      // Start the retry sequence
      attemptClearHistory();
    };

    // Add event listener for canvas undo history reset
    window.addEventListener(
      "canvas-reset-undo-history",
      handleResetUndoHistory
    );

    return () => {
      window.removeEventListener(
        "canvas-reset-undo-history",
        handleResetUndoHistory
      );
    };
  }, [clearEditorUndoHistory]);

  // Update undo/redo availability - improved logic with safeguards
  useEffect(() => {
    const updateUndoRedoState = () => {
      try {
        const editor = window.canvasBlockNoteEditor;
        if (editor && editor._tiptapEditor) {
          const tiptapEditor = editor._tiptapEditor;

          let canUndoValue = false;
          let canRedoValue = false;

          try {
            // Method 1: Try using the can() chain if it exists (most reliable)
            if (tiptapEditor.can && typeof tiptapEditor.can === "function") {
              const canChain = tiptapEditor.can();
              if (canChain && typeof canChain.undo === "function") {
                canUndoValue = canChain.undo();
              }
              if (canChain && typeof canChain.redo === "function") {
                canRedoValue = canChain.redo();
              }
            }
          } catch {
            // Fallback methods if can() is not available
            try {
              // Method 2: Check history state directly
              if (tiptapEditor.state && tiptapEditor.state.history) {
                const history = tiptapEditor.state.history;
                canUndoValue = history.done && history.done.length > 0;
                canRedoValue = history.undone && history.undone.length > 0;
              } else if (tiptapEditor.state && tiptapEditor.state.history$) {
                const history = tiptapEditor.state.history$;
                canUndoValue =
                  history.prevRanges && history.prevRanges.length > 0;
                canRedoValue =
                  history.nextRanges && history.nextRanges.length > 0;
              }
            } catch {
              // Method 3: Final fallback - check if commands exist but be conservative
              if (tiptapEditor.commands) {
                const hasUndoCommand =
                  typeof tiptapEditor.commands.undo === "function";
                const hasRedoCommand =
                  typeof tiptapEditor.commands.redo === "function";

                // Only enable if commands exist AND we have established history
                // Be conservative to prevent undoing back to empty content
                canUndoValue =
                  hasUndoCommand &&
                  canUndo &&
                  editor.document &&
                  editor.document.length > 1;
                canRedoValue = hasRedoCommand && canRedo;
              }
            }
          }

          // Additional safeguard: Don't allow undo if it would result in empty content
          if (canUndoValue !== canUndo) {
            setCanUndo(canUndoValue);
          }
          if (canRedoValue !== canRedo) {
            setCanRedo(canRedoValue);
          }
        } else {
          // No editor available
          if (canUndo) setCanUndo(false);
          if (canRedo) setCanRedo(false);
        }
      } catch (error) {
        console.warn(
          "🎨 CanvasToolbar: Error updating undo/redo state:",
          error
        );
        // On error, disable both buttons for safety
        if (canUndo) setCanUndo(false);
        if (canRedo) setCanRedo(false);
      }
    };

    // Update state immediately
    updateUndoRedoState();

    // Set up periodic checking but with reduced frequency to avoid performance issues
    const interval = setInterval(updateUndoRedoState, 1000); // Check every second

    return () => clearInterval(interval);
  }, [canUndo, canRedo, clearEditorUndoHistory]); // Include current state in dependencies

  // Handle save functionality
  const handleSave = useCallback(async () => {
    if (!canvasSourceMessageId) {
      console.error("🎨 CanvasToolbar: Cannot save - no source message ID");
      toast.error("Cannot save: Canvas was not opened from a message");
      return;
    }

    if (!canvasContent || canvasContent.trim() === "") {
      console.warn("🎨 CanvasToolbar: Warning - saving empty canvas");
      toast.warning("Saving empty canvas content");
    }

    setIsLoading((prev) => ({ ...prev, save: true }));

    try {
      const success = await saveCanvas();

      if (success) {
        toast.success("Canvas saved successfully");
      } else {
        console.error(
          "🎨 CanvasToolbar: Save failed - saveCanvas returned false"
        );
        toast.error("Failed to save canvas - please check console for details");
      }
    } catch (error) {
      console.error("🎨 CanvasToolbar: Save error:", error);
      toast.error(`Failed to save canvas: ${error.message || "Unknown error"}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  }, [
    saveCanvas,
    canvasSourceMessageId,
    canvasContent,
  ]);

  // Handle copy functionality
  const handleCopy = useCallback(async () => {
    setIsLoading((prev) => ({ ...prev, copy: true }));
    try {
      const success = await copyCanvasContent();
      if (success) {
        // Show success animation
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 1500);

        // Show appropriate toast message based on canvas type
        if (canvasType === "spreadsheet") {
          toast.success("Spreadsheet copied as markdown table");
        } else {
          toast.success("Content copied to clipboard");
        }
      } else {
        toast.error("Failed to copy content");
      }
    } catch (error) {
      console.error("Copy error:", error);
      toast.error("Failed to copy content");
    } finally {
      setIsLoading((prev) => ({ ...prev, copy: false }));
    }
  }, [copyCanvasContent, canvasType]);

  // Helper function to download a blob
  const downloadBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Handle download functionality - uses backend API for PDF/DOCX/Excel
  const handleDownload = useCallback(
    async (format) => {
      if (!canvasContent) return;

      const filename = canvasName || "document";
      const language = canvasState?.language || "javascript";
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3002";

      setIsLoading((prev) => ({ ...prev, download: true }));

      try {
        switch (format) {
          case "markdown": {
            // For code canvases, wrap content in code fence
            let content = canvasContent;
            if (canvasType === "code") {
              content = `\`\`\`${language}\n${canvasContent}\n\`\`\``;
            }
            const blob = new Blob([content], { type: "text/markdown" });
            downloadBlob(blob, `${filename}.md`);
            toast.success(`Downloaded as ${filename}.md`);
            break;
          }
          case "pdf": {
            toast.loading("Generating PDF...", { id: "pdf-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as PDF");
            }
            // For code canvases, wrap in code fence for proper PDF rendering
            let pdfContent = canvasContent;
            if (canvasType === "code") {
              pdfContent = `\`\`\`${language}\n${canvasContent}\n\`\`\``;
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/markdown-to-pdf`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                markdown: pdfContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate PDF");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.pdf`);
            toast.success(`Downloaded as ${filename}.pdf`, { id: "pdf-download" });
            break;
          }
          case "docx": {
            toast.loading("Generating Word document...", { id: "docx-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as Word document");
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/markdown-to-docx`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                markdown: canvasContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate DOCX");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.docx`);
            toast.success(`Downloaded as ${filename}.docx`, { id: "docx-download" });
            break;
          }
          case "source": {
            // Download raw code with language-specific extension
            const ext = LANGUAGE_EXTENSIONS[language] || ".txt";
            const mimeType = getMimeType(language);
            const blob = new Blob([canvasContent], { type: mimeType });
            downloadBlob(blob, `${filename}${ext}`);
            toast.success(`Downloaded as ${filename}${ext}`);
            break;
          }
          case "csv": {
            // Download spreadsheet content as CSV
            const blob = new Blob([canvasContent], { type: "text/csv" });
            downloadBlob(blob, `${filename}.csv`);
            toast.success(`Downloaded as ${filename}.csv`);
            break;
          }
          case "excel": {
            // Convert CSV to Excel via backend API
            toast.loading("Generating Excel spreadsheet...", { id: "excel-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as Excel");
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/csv-to-excel`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                csv: canvasContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate Excel");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.xlsx`);
            toast.success(`Downloaded as ${filename}.xlsx`, { id: "excel-download" });
            break;
          }
          case "html": {
            // Download HTML content
            const blob = new Blob([canvasContent], { type: "text/html" });
            downloadBlob(blob, `${filename}.html`);
            toast.success(`Downloaded as ${filename}.html`);
            break;
          }
          default: {
            const blob = new Blob([canvasContent], { type: "text/plain" });
            downloadBlob(blob, `${filename}.txt`);
            toast.success(`Downloaded as ${filename}.txt`);
          }
        }
      } catch (error) {
        console.error("Download error:", error);
        toast.error(`Failed to download as ${format.toUpperCase()}`, { id: `${format}-download` });
      } finally {
        setIsLoading((prev) => ({ ...prev, download: false }));
      }
    },
    [canvasContent, canvasName, canvasType, canvasState?.language, downloadBlob]
  );

  // Handle reset functionality using BlockNote's native method
  const _handleReset = useCallback(() => {
    const editor = window.canvasBlockNoteEditor;
    if (editor && canvasState.originalContent) {
      try {
        // If original content is blocks, use replaceBlocks
        if (Array.isArray(canvasState.originalContent)) {
          editor.replaceBlocks(editor.document, canvasState.originalContent);
        } else {
          // If original content is string, convert to blocks first
          const blocks = [
            {
              id: editor.schema.generateId(),
              type: "paragraph",
              props: {},
              content: [
                {
                  type: "text",
                  text: canvasState.originalContent,
                  styles: {},
                },
              ],
              children: [],
            },
          ];
          editor.replaceBlocks(editor.document, blocks);
        }
        toast.success("Canvas reset to original state");
      } catch (error) {
        console.error("Reset error using BlockNote:", error);
        // Fallback to context method
        resetCanvas();
        toast.success("Canvas reset to original state");
      }
    } else {
      // Fallback to context method if editor not available
      resetCanvas();
      toast.success("Canvas reset to original state");
    }
  }, [canvasState.originalContent, resetCanvas]);

  // Handle share functionality - updated to use dialog
  const handleShare = useCallback(async () => {
    try {
      if (!canvasContent || !canvasContent.trim()) {
        toast.error("No content to share");
        return;
      }

      // Open the share dialog instead of native sharing
      setShowShareDialog(true);
    } catch (error) {
      console.error("Share error:", error);
      toast.error("Failed to open share dialog");
    }
  }, [canvasContent]);

  // Handle rename functionality
  const handleStartRename = useCallback(() => {
    setEditingName(canvasName || "Canvas");
    setIsEditing(true);
    setShowRenameDropdown(false);
    // Focus the input after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  }, [canvasName]);

  const handleCancelRename = useCallback(() => {
    setIsEditing(false);
    setEditingName("");
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingName.trim()) {
      toast.error("Canvas name cannot be empty");
      return;
    }

    if (editingName.trim() === canvasName) {
      // No change, just exit editing mode
      setIsEditing(false);
      setEditingName("");
      return;
    }

    setIsLoading((prev) => ({ ...prev, rename: true }));
    try {
      const success = await renameCanvas(editingName.trim());
      if (success) {
        toast.success("Canvas renamed successfully");
        setIsEditing(false);
        setEditingName("");
      } else {
        toast.error("Failed to rename canvas");
      }
    } catch (error) {
      console.error("Rename error:", error);
      toast.error("Failed to rename canvas");
    } finally {
      setIsLoading((prev) => ({ ...prev, rename: false }));
    }
  }, [editingName, canvasName, renameCanvas]);

  const handleRenameKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSaveRename();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancelRename();
      }
    },
    [handleSaveRename, handleCancelRename]
  );

  // Handle preview toggle for code content
  const handleTogglePreview = useCallback(() => {
    setShowPreview(!showPreview);

    // Add a small delay to log the new state
    setTimeout(() => {}, 100);
  }, [showPreview, setShowPreview]);

  // Check if current canvas content is code that can be previewed
  const canShowPreview = useMemo(() => {
    if (canvasType !== "code") {
      return false;
    }

    // Extract actual code content from markdown
    const actualContent = extractCodeContent(canvasContent);

    // For debugging, let's also check for any JavaScript/JSX patterns
    const hasJavaScript =
      actualContent.includes("function") ||
      actualContent.includes("const ") ||
      actualContent.includes("let ") ||
      actualContent.includes("var ") ||
      actualContent.includes("=>");

    // Check if content contains React code OR any JavaScript (for testing)
    const canPreview =
      actualContent.includes("import React") ||
      actualContent.includes("export default") ||
      actualContent.includes("from 'react'") ||
      actualContent.includes('from "react"') ||
      hasJavaScript; // Add this for testing

    return canPreview;
  }, [canvasType, canvasContent]);

  const _handleTestStream = async () => {
    try {
      await requestCanvasStream(
        "Create a simple JavaScript function that adds two numbers",
        {
          type: "code",
          language: "javascript",
          useStreaming: true,
        }
      );
    } catch (error) {
      console.error("Error testing canvas stream:", error);
    }
  };

  const _handleTestBasicCanvas = () => {
    try {
      openCanvas({
        content: "This is a test canvas with **bold text** and normal text.",
        type: "text",
        language: "javascript",
        metadata: {
          source: "test_button",
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error("Error testing basic canvas:", error);
    }
  };

  const buttonClass =
    "h-8 w-8 p-0 bg-transparent border-0 shadow-none hover:bg-white/10 dark:hover:bg-white/10 hover:bg-gray-900/10 focus:bg-transparent active:bg-transparent transition-colors";

  return (
    <>
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-gray-600 dark:border-gray-600 border-gray-300"
        data-testid={TEST_IDS.CANVAS_TOOLBAR}
        style={{
          backgroundColor: document.documentElement.classList.contains("dark")
            ? "#2F2F2F"
            : "#FAFAFA",
        }}
      >
        {/* Left section - Close and Title */}
        <div className="flex items-center gap-3">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    console.log("🎨 [CanvasToolbar] Close button clicked", {
                      onClose: typeof onClose,
                      hasOnClose: !!onClose,
                    });
                    if (onClose && typeof onClose === "function") {
                      try {
                        onClose();
                      } catch (error) {
                        console.error(
                          "🎨 [CanvasToolbar] Error calling onClose:",
                          error
                        );
                      }
                    } else {
                      console.warn(
                        "🎨 [CanvasToolbar] onClose is not a function!",
                        {
                          onClose,
                          type: typeof onClose,
                        }
                      );
                    }
                  }}
                  className={buttonClass}
                  data-testid={TEST_IDS.CANVAS_CLOSE_BUTTON}
                  style={{
                    color: document.documentElement.classList.contains("dark")
                      ? "#FFFFFF"
                      : "#1a1a1a",
                  }}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Close canvas</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="flex items-center gap-3">
            {/* Canvas Title with Rename Functionality */}
            {isEditing ? (
              <Input
                ref={inputRef}
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                onBlur={handleSaveRename}
                className="h-7 px-2 py-1 text-sm font-medium border focus:border-purple-400 focus:ring-1 focus:ring-purple-400 min-w-[120px] max-w-[200px]"
                style={{
                  backgroundColor: document.documentElement.classList.contains(
                    "dark"
                  )
                    ? "#374151"
                    : "#FFFFFF",
                  borderColor: document.documentElement.classList.contains(
                    "dark"
                  )
                    ? "#4B5563"
                    : "#D1D5DB",
                  color: document.documentElement.classList.contains("dark")
                    ? "#FFFFFF"
                    : "#1a1a1a",
                }}
                disabled={isLoading.rename}
              />
            ) : (
              <DropdownMenu
                open={showRenameDropdown}
                onOpenChange={setShowRenameDropdown}
              >
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="text-sm font-medium hover:text-gray-300 dark:hover:text-gray-300 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 dark:hover:bg-white/5 hover:bg-gray-900/5 bg-transparent border-0"
                          style={{
                            color: document.documentElement.classList.contains(
                              "dark"
                            )
                              ? "#FFFFFF"
                              : "#1a1a1a",
                          }}
                        >
                          <span>{canvasName || "Canvas"}</span>
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Edit name</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent
                  align="start"
                  className="w-48 backdrop-blur-md shadow-lg rounded-2xl"
                  style={{
                    zIndex: 1000,
                    backgroundColor:
                      document.documentElement.classList.contains("dark")
                        ? "rgba(63, 63, 63, 0.95)"
                        : "rgba(255, 255, 255, 0.95)",
                    borderColor: document.documentElement.classList.contains(
                      "dark"
                    )
                      ? "#4B5563"
                      : "#D1D5DB",
                  }}
                >
                  <DropdownMenuItem
                    onClick={handleStartRename}
                    className="flex items-center gap-2 py-2 px-3 hover:bg-white/10 dark:hover:bg-white/10 hover:bg-gray-900/10 cursor-pointer rounded-xl"
                    style={{
                      color: document.documentElement.classList.contains("dark")
                        ? "#FFFFFF"
                        : "#1a1a1a",
                    }}
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>Rename</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Last Saved Indicator */}
            {canvasState.metadata?.lastSaved && !canvasIsDirty && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                • Saved{" "}
                {(() => {
                  const savedTime = new Date(canvasState.metadata.lastSaved);
                  const now = new Date();
                  const diffMs = now - savedTime;
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);

                  if (diffMs < 60000) return "just now";
                  if (diffMins < 60) return `${diffMins}m ago`;
                  if (diffHours < 24) return `${diffHours}h ago`;
                  return savedTime.toLocaleDateString();
                })()}
              </span>
            )}

            {/* Unsaved Changes Indicator */}
            {canvasIsDirty && (
              <span className="text-xs text-yellow-500 dark:text-yellow-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 dark:bg-yellow-400"></span>
                Unsaved changes
              </span>
            )}

            {/* Version Dropdown - COMMENTED OUT FOR NOW */}
            {/* <div className="flex items-center gap-2">
                <VersionDropdown
                  canvasName={canvasName}
                  canvasVersion={canvasVersion}
                />
              </div> */}
          </div>
        </div>

        {/* Right section - Action buttons */}
        <div className="flex items-center gap-1">
          {/* Save Button - show if canvas has source message */}
          {canvasSourceMessageId ? (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`${buttonClass} ${isLoading.save ? "opacity-50" : ""}`}
                    onClick={handleSave}
                    disabled={isLoading.save || canvasIsLoading}
                    style={{
                      color: document.documentElement.classList.contains("dark")
                        ? "#FFFFFF"
                        : "#1a1a1a",
                    }}
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Save changes</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            /* Debug: Show disabled save button with warning if no sourceMessageId */
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`${buttonClass} opacity-50 cursor-not-allowed`}
                    disabled
                    style={{
                      color: document.documentElement.classList.contains("dark")
                        ? "#888888"
                        : "#999999",
                    }}
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Cannot save: Canvas not opened from message</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Reset to Original Button - Commented Out */}
          {/* <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={buttonClass}
                    onClick={handleReset}
                  >
                    <RotateCcw className="h-4 w-4 text-white" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Reset to original</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> */}

          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`${buttonClass} ${!canUndo ? "opacity-50" : ""}`}
                  onClick={handleUndo}
                  disabled={!canUndo}
                  style={{
                    color: document.documentElement.classList.contains("dark")
                      ? "#FFFFFF"
                      : "#1a1a1a",
                  }}
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Undo</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`${buttonClass} ${!canRedo ? "opacity-50" : ""}`}
                  onClick={handleRedo}
                  disabled={!canRedo}
                  style={{
                    color: document.documentElement.classList.contains("dark")
                      ? "#FFFFFF"
                      : "#1a1a1a",
                  }}
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Redo</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="w-px h-6 bg-gray-600 dark:bg-gray-600 bg-gray-300 mx-1" />

          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`${buttonClass} ${isLoading.copy ? "opacity-50" : ""}`}
                  onClick={handleCopy}
                  disabled={isLoading.copy}
                  style={{
                    color: document.documentElement.classList.contains("dark")
                      ? "#FFFFFF"
                      : "#1a1a1a",
                  }}
                >
                  <AnimatePresence mode="wait">
                    {showCopySuccess ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Check
                          className="h-4 w-4"
                          style={{
                            color: document.documentElement.classList.contains(
                              "dark"
                            )
                              ? "#4ade80"
                              : "#16a34a",
                          }}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Copy className="h-4 w-4" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Copy to clipboard</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Export Dropdown */}
          <DropdownMenu>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`${buttonClass} ${isLoading.download ? "opacity-50" : ""}`}
                      disabled={isLoading.download}
                      style={{
                        color: document.documentElement.classList.contains(
                          "dark"
                        )
                          ? "#FFFFFF"
                          : "#1a1a1a",
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Download options</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent
              align="end"
              className="min-w-[220px] bg-transparent dark:bg-transparent bg-white/95 backdrop-blur-md border-gray-600 dark:border-gray-600 border-gray-300 shadow-lg rounded-2xl"
              style={{ zIndex: 1000 }}
            >
              {getDownloadOptions(canvasType, canvasState?.language).map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleDownload(option.id)}
                  disabled={isLoading.download}
                  className="flex items-center gap-2 py-2 px-3 text-white dark:text-white text-gray-900 hover:bg-white/10 dark:hover:bg-white/10 hover:bg-gray-900/10 cursor-pointer rounded-xl"
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={buttonClass}
                  onClick={handleShare}
                  style={{
                    color: document.documentElement.classList.contains("dark")
                      ? "#FFFFFF"
                      : "#1a1a1a",
                  }}
                >
                  <Share className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Share canvas</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Preview Toggle Button - positioned after share button with rounded style */}
          {canShowPreview && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleTogglePreview}
                    variant="ghost"
                    size="sm"
                    data-testid={TEST_IDS.CANVAS_PREVIEW_TOGGLE}
                    className={`
                        flex items-center gap-2 px-3 py-1 h-8 ml-2
                        bg-gray-700 dark:bg-gray-700 bg-white hover:bg-gray-600 dark:hover:bg-gray-600 hover:bg-gray-100
                        rounded-full border border-gray-600 dark:border-gray-600 border-gray-300
                        text-white dark:text-white text-gray-900 text-sm font-medium
                        transition-all duration-200
                        ${showPreview ? "bg-gray-600 dark:bg-gray-600 bg-gray-100" : ""}
                      `}
                  >
                    {showPreview ? (
                      <Code className="h-4 w-4" />
                    ) : (
                      <PlayCircle className="h-4 w-4" />
                    )}
                    <span>{showPreview ? "Code" : "Preview"}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>
                    {showPreview ? "Show code editor" : "Show live preview"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Canvas Share Dialog */}
      <CanvasShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        content={canvasContent || ""}
        title={canvasName || "Canvas"}
        sourceMessageId={canvasSourceMessageId}
        canvasType={canvasType || "text"}
      />
    </>
  );
});

CanvasToolbar.displayName = "CanvasToolbar";

CanvasToolbar.propTypes = {
  onClose: PropTypes.func,
};

export default CanvasToolbar;
